import * as React from 'react';
import { getCellProperty } from './getCellProperty';
import { keyCodes } from './keyCodes';
import {
  inNumericKey,
  isNavigationKey,
  isNumpadNumericKey,
  isAllowedOnNumberTypingKey,
  isCharAllowedOnNumberInput
} from './keyCodeCheckings';
import { getCharFromKey } from './getCharFromKeyCode';
import { parseLocaleNumber } from './parseLocaleNumber';
import styled from 'styled-components';
import { TbArrowDown, TbArrowUp, TbClipboardText } from 'react-icons/tb';
import commonStore from '../../store/common';
import reportStore from '../../pages/Report/store';
import { TbMath } from 'react-icons/tb';
import { IoCloseOutline } from 'react-icons/io5';
import { FiInfo } from 'react-icons/fi';
import { IoCheckmarkDoneOutline } from 'react-icons/io5';
import ReportCellComment from '../../pages/Report/components/ReportComments/ReportCellComment';
import { Flex, Text } from '@mantine/core';
import { getCellState, isCellEmpty } from '../../utils/cellTemplate';

// Lazy-mounts the comment icon/popover after the first hover (see SelectCellView
// in NewSelectCellTemplate for the rationale). Only used in display mode.
const NumberCellView = ({ cell }: { cell: any }) => {
  const [commentMounted, setCommentMounted] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.text);
  const isWarning = cell?.warning?.status === 'Warning';
  const { isInvalid, isDisabled, background: baseBackground } = getCellState(cell, isEmptyValue);

  const background = !isInvalid && isWarning ? 'rgba(250, 176, 5, .1)' : baseBackground;
  return (
    <ValueContainer
      style={{
        background
      }}
      className="report-field"
      data-key={`${cell.dataKey}-${cell.dataIndex}-${cell.tableName}-${cell.fundId}${cell.sub_asset_type ? `-${cell.sub_asset_type}` : ''}`}
      hasDrillDown={cell.hasDrillDownFeature}
      onPointerEnter={() => setCommentMounted(true)}
    >
      {cell?.warning && cell?.warning?.status === 'Not Validated' && (
        <div className="validation">
          <IoCloseOutline fontSize="16px" color="#fa5252" />
          <p className="warning-text">Not Validated</p>
          <FiInfo
            data-tooltip-id="dark-tooltip-2"
            data-tooltip-content={cell.warning.msg}
            color="#7888f5"
            fontSize="16px"
          />
        </div>
      )}
      {cell?.warning && cell?.warning?.status === 'Validated' && (
        <div className="validation">
          <IoCheckmarkDoneOutline fontSize="16px" color="#40C057" />
          <p
            className="warning-text"
            style={{
              color: '#40C057'
            }}
          >
            Validated
          </p>
          <FiInfo
            data-tooltip-id="dark-tooltip-2"
            data-tooltip-content={cell.warning.msg}
            color="#7888f5"
            fontSize="16px"
          />
        </div>
      )}
      <div className="text">
        <p
          style={{
            color: isDisabled ? '#909296' : isEmptyValue && cell.required ? '#FF6B6B' : '#000',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          {isEmptyValue && cell.required ? 'This field is mandatory' : cell.text}
          {cell?.difference?.is_changed && (
            <Flex align="center" gap="4px">
              <Text fz="12px" c="orange.6">
                {cell.difference.change_percent}%
              </Text>
              {cell.difference.change_direction === 'up' ? (
                <TbArrowUp fontSize="16px" color="var(--mantine-color-orange-6)" />
              ) : cell.difference.change_direction === 'down' ? (
                <TbArrowDown fontSize="16px" color="var(--mantine-color-orange-6)" />
              ) : (
                ''
              )}
            </Flex>
          )}
        </p>
        <div className="elements">
          {cell.text && cell.isStaticField && (
            <TbClipboardText style={{ minWidth: '12px' }} fontSize="12px" />
          )}
          {cell.hasDrillDownFeature && !reportStore.unplainedIsConversionReportType && (
            <TbMath
              onClick={() =>
                commonStore.setDrillDown({
                  report_id: cell.reportId,
                  fund_id: cell.fundId,
                  table_name: cell.tableName,
                  column_name: cell.dataKey,
                  table_id: cell.tableId,
                  column_pretty_name: cell.title
                })
              }
              color="#7888F5"
              fontSize="15px"
            />
          )}
        </div>
      </div>
      {!!cell.errorMessage && <p className="error-message">{cell.errorMessage}</p>}
      {cell?.warning?.status === 'Warning' && (
        <p
          style={{
            color: '#fab005'
          }}
          className="error-message"
        >
          {cell?.warning?.msg}
        </p>
      )}
      <ReportCellComment
        fundId={cell.fundId}
        fieldName={cell.dataKey}
        fundName={cell.fundName}
        tableName={cell.tableName}
        fieldOrder={cell.fieldOrder}
        cellHovered={commentMounted}
      />
    </ValueContainer>
  );
};

export class NewNumberCellTemplate {
  wasEscKeyPressed = false;

  getCompatibleCell(uncertainCell: any) {
    let value;
    try {
      value = getCellProperty(uncertainCell, 'value', 'number');
    } catch (error) {
      value = NaN;
    }
    const numberFormat = uncertainCell.format || new Intl.NumberFormat(window.navigator.language);
    const displayValue = uncertainCell.nanToZero && Number.isNaN(value) ? 0 : value;
    const text =
      Number.isNaN(displayValue) || (uncertainCell.hideZero && displayValue === 0)
        ? ''
        : displayValue.toLocaleString('en-US', { maximumFractionDigits: 20 });
    return { ...uncertainCell, value: displayValue, text };
  }

  handleKeyDown(
    cell: any,
    keyCode: number,
    ctrl: boolean,
    _shift: boolean,
    _alt: boolean,
    key: string,
    _capsLock: boolean
  ) {
    if (isNumpadNumericKey(keyCode)) keyCode -= 48;
    const char = getCharFromKey(key);
    if (!ctrl && isCharAllowedOnNumberInput(char)) {
      const value = Number(char);

      if (Number.isNaN(value) && isCharAllowedOnNumberInput(char))
        return {
          cell: { ...this.getCompatibleCell({ ...cell, value }), text: char },
          enableEditMode: true
        };
      return {
        cell: this.getCompatibleCell({ ...cell, value }),
        enableEditMode: true
      };
    }
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  update(cell: any, cellToMerge: any) {
    return this.getCompatibleCell({ ...cell, value: cellToMerge.value });
  }

  getTextFromCharCode = (cellText: string) => {
    switch (cellText.charCodeAt(0)) {
      case keyCodes.DASH:
      case keyCodes.FIREFOX_DASH:
      case keyCodes.SUBTRACT:
        return '-';
      case keyCodes.COMMA:
        return ',';
      case keyCodes.PERIOD:
      case keyCodes.DECIMAL:
        return '.';
      default:
        return cellText;
    }
  };

  getClassName(cell: any, _isInEditMode: boolean) {
    const isValid = cell.validator?.(cell.value) ?? true;
    const className = cell.className || '';
    return `${!isValid ? 'rg-invalid' : ''} ${className}`;
  }

  render(cell: any, isInEditMode: boolean, onCellChanged: (cell: any, commit: boolean) => void) {
    if (!isInEditMode) {
      return <NumberCellView cell={cell} />;
    }

    const locale = cell.format
      ? cell.format.resolvedOptions().locale
      : window.navigator.languages[0];
    const format = new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20
    });

    return (
      <StyledInput
        className="rg-input"
        inputMode="decimal"
        ref={input => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        defaultValue={
          Number.isNaN(cell.value) ? this.getTextFromCharCode(cell.text) : format.format(cell.value)
        }
        disabled={(!cell.isStaticTemplate && cell.isStaticField) || commonStore.isViewer}
        onChange={e =>
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              value: parseLocaleNumber(e.currentTarget.value)
            }),
            false
          )
        }
        onBlur={e => {
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              value: parseLocaleNumber(e.currentTarget.value)
            }),
            !this.wasEscKeyPressed
          );
          this.wasEscKeyPressed = false;
        }}
        onKeyDown={e => {
          const isModifierShortcut = e.ctrlKey || e.metaKey;

          if (isModifierShortcut) {
            e.stopPropagation();
            return;
          }

          if (
            inNumericKey(e.keyCode) ||
            isNavigationKey(e.keyCode) ||
            isAllowedOnNumberTypingKey(e.keyCode)
          )
            e.stopPropagation();

          if (
            !inNumericKey(e.keyCode) &&
            !isNavigationKey(e.keyCode) &&
            !isCharAllowedOnNumberInput(getCharFromKey(e.key))
          )
            e.preventDefault();
          if (e.keyCode === keyCodes.ESCAPE) this.wasEscKeyPressed = true;
        }}
        onCopy={e => e.stopPropagation()}
        onCut={e => e.stopPropagation()}
        onPaste={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        style={{
          border: 'none'
        }}
      />
    );
  }
}

// export default observer(NewNumberCellTemplate);

const StyledInput = styled.input`
  font-size: 12px !important;
  line-height: 17px !important;
  &:disabled {
    color: #909296 !important;
  }
`;

const ValueContainer = styled.div<{ hasDrillDown?: boolean }>`
  padding: 6px 8px;
  width: 100%;
  height: 100%;

  .cell-comment-container {
    // because drill down icon occupies the right side
    right: ${({ hasDrillDown }) => (hasDrillDown ? 30 : 8)}px;
  }

  &:hover {
    .cell-comment-container {
      visibility: visible;
    }
  }

  .text {
    width: 100%;
    height: fit-content;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #000;
    font-size: 12px;
    line-height: 17px;
  }
  .error-message {
    color: #ff6b6b;
    font-size: 12px;
    line-height: 17px;
    white-space: initial;
  }
  .elements {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .validation {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    justify-content: flex-end;
    .warning-text {
      font-size: 12px;
      line-height: 16px;
      color: #fa5252;
    }
  }
`;
